
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { signIn, signUp } from "@/lib/auth";
import { translateAuthError, emailConfirmationMessage } from "@/lib/auth-errors";
import { apiFetch } from "@/lib/api-client";

export const useAuthForm = (onResetClick: () => void, onSuccess?: () => void, redirectUrl?: string) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmMessage, setShowConfirmMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const toggleForm = () => {
    setIsLogin(!isLogin);
    setShowConfirmMessage(false);
    setErrorMessage(null);
    setEmail("");
    setPassword("");
    setFullName("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setShowConfirmMessage(false);
    setErrorMessage(null);
    
    try {
      if (isLogin) {
        // Login
        console.log("Attempting login for:", email);
        const { error } = await signIn(email, password);
        if (error) throw error;
        
        toast({
          title: "Edukalt sisse logitud",
          description: "Tere tulemast tagasi!",
        });
        
        // If there's a success callback, call it instead of navigating
        if (onSuccess) {
          onSuccess();
        } else {
          navigate("/");
        }
      } else {
        // Sign up
        if (!fullName.trim()) {
          throw new Error("Palun sisesta oma täisnimi");
        }
        
        console.log("Attempting signup for:", email);
        
        // Use redirectUrl if provided
        const signUpOptions = redirectUrl ? { redirectTo: redirectUrl } : undefined;
        console.log("Signup with redirect options:", signUpOptions);
        
        const { data, error } = await signUp(email, password, fullName, signUpOptions);
        
        console.log("Signup response:", data ? "Data returned" : "No data", error ? `Error: ${error.message}` : "No error");
        
        if (error) {
          // Check for timeout errors
          if (error.message?.includes("timeout") || error.message?.includes("aegus")) {
            setErrorMessage("E-kirja saatmine aegus. Supabase serveril võtab vastamine liiga kaua aega. Palun kasuta teist e-posti aadressi või proovi hiljem uuesti.");
          }
          // Check for rate limiting errors
          else if (error.message?.includes("rate limit") || error.message?.includes("429")) {
            setErrorMessage("Liiga palju katseid. Palun proovi mõne minuti pärast uuesti või kasuta teist e-posti aadressi.");
          }
          
          throw error;
        }
        
        // If signup was successful, add user to MailerLite (best-effort)
        try {
          await apiFetch('/api/mailerlite/subscribe', {
            method: 'POST',
            body: JSON.stringify({ email, name: fullName }),
          });
        } catch (mailerliteError) {
          console.error("Failed to add user to MailerLite:", mailerliteError);
          // Continue with signup success flow despite MailerLite error
        }
        
        // Always show confirmation message on successful signup attempt
        setShowConfirmMessage(true);
        setEmail("");
        setPassword("");
        setFullName("");
        
        toast({
          title: "Konto loomine õnnestus",
          description: emailConfirmationMessage,
          duration: 10000, // Longer duration so user has time to read
        });
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
      
      // Show translated error message
      const translatedError = translateAuthError(error.message);
      setErrorMessage(translatedError);
      
      toast({
        title: "Autentimine ebaõnnestus",
        description: translatedError,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    isLogin,
    email,
    setEmail,
    password,
    setPassword,
    fullName,
    setFullName,
    showPassword,
    setShowPassword,
    loading,
    showConfirmMessage,
    errorMessage,
    handleSubmit,
    toggleForm,
    onResetClick
  };
};
